// src/context/CartContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";

import axios from "axios";

import { toast } from "react-toastify";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  addItem,
  removeItem,
  setCart,
} from "../Redux/CartSlice";

import {
  getCartByUserId,
} from "../services/Cart/CartPage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://admin.medrphalabs.com/api/";
const CART_API = `${API_BASE_URL}MasterAPI/AddToCart`;
const DELETE_CART_API = `${API_BASE_URL}MasterAPI/DeleteCart`;

const CartContext = createContext();

export const CartProvider = ({
  children,
}) => {

  const dispatch = useDispatch();

  const cart = useSelector(
    (state) => state.cart.items || []
  );

  // ✅ USER GET
  const user = JSON.parse(
    localStorage.getItem("medarphaUser") || "{}"
  );

  /* =========================================================
     ✅ AUTO LOAD CART AFTER LOGIN
  ========================================================= */

  useEffect(() => {

    const loadCart = async () => {

      // ✅ USER NOT LOGIN
      if (!user?.userId) {

        console.log("❌ USER NOT FOUND");

        dispatch(setCart([]));

        return;
      }

      try {

        console.log("👤 LOGIN USER:", user);

        console.log("🆔 USER ID:", user.userId);

        const { cartId, items } =
          await getCartByUserId(user.userId);

        console.log(
          "🛒 AUTO LOAD CART:",
          items
        );

        if (cartId) {

          localStorage.setItem(
            "cartId",
            cartId
          );
        }

        dispatch(setCart(items || []));

      } catch (err) {

        console.error(
          "❌ Error loading cart:",
          err
        );
      }
    };

    loadCart();

  }, [dispatch, user?.userId]);

  /* =========================================================
     ✅ SAVE CART LOCAL STORAGE
  ========================================================= */

  useEffect(() => {

    const timer = setTimeout(() => {

      localStorage.setItem(
        "cart",
        JSON.stringify(cart)
      );

    }, 200);

    return () => clearTimeout(timer);

  }, [cart]);

  /* =========================================================
     ✅ DELETE FULL CART
  ========================================================= */

  const deleteCartOnServer = async (
    cartId
  ) => {

    if (
      !cartId ||
      cartId === "0"
    )
      return;

    try {

      const resp = await axios.post(
        DELETE_CART_API,
        {
          cardId: Number(cartId),
        }
      );

      if (resp?.data?.succeeded)
        return resp.data;

      else
        throw new Error(
          resp?.data?.message ||
            "Failed to delete cart on server"
        );

    } catch (err) {

      console.error(
        "Delete cart API error:",
        err.response?.data ||
          err.message
      );

      throw err;
    }
  };

  /* =========================================================
     ✅ ADD TO CART
  ========================================================= */

  const addToCart = async (
    product
  ) => {

    if (!user?.userId) {
      toast.warn("Please login to add items to cart");
      window.dispatchEvent(new CustomEvent("trigger-login-popup"));
      return;
    }

    // ✅ PRICE CALCULATION

    const originalPrice = Number(
      product.TestPrice ??
        product.testPrice ??
        product.packagePrice ??
        0
    );

    const price = Number(
      product.SellingPrice ??
        product.price ??
        originalPrice
    );

    const discount =
      originalPrice - price;

    // ✅ PAYLOAD

    const payload = {
      userId: user.userId,

      productId:
        product.productId ??
        product.TestID,

      categoryId:
        product.categoryId ?? 2,

      totalQuantity: 1,

      price,

      discount,

      originalPrice,

      coupon: "",

      storeId:
        product.labId ??
        product.storeId,

      ItemName:
        product.name ??
        product.TestName ??
        product.packageName ??
        "",
    };

    try {

      console.log(
        "🟢 addToCart payload:",
        payload
      );

      const resp = await axios.post(
        CART_API,
        payload
      );

      if (
        resp?.data?.succeeded ||
        resp?.status === 200
      ) {

        dispatch(
          addItem({
            productId:
              payload.productId,

            categoryId:
              payload.categoryId,

            name:
              payload.ItemName,

            price:
              payload.price,

            discount:
              payload.discount,

            quantity: 1,

            storeId:
              payload.storeId,

            originalPrice:
              payload.originalPrice,
          })
        );

        // ✅ AUTO REFRESH CART
        const {
          cartId,
          items,
        } =
          await getCartByUserId(
            user.userId
          );

        console.log(
          "🛒 UPDATED CART:",
          items
        );

        dispatch(
          setCart(items || [])
        );

        if (cartId) {

          localStorage.setItem(
            "cartId",
            cartId
          );
        }

        toast.success(
          "Item added to cart ✅"
        );

      } else {

        toast.error(
          "Failed to add item to server cart"
        );
      }

    } catch (err) {

      console.error(
        "❌ Add to cart error:",
        err.response?.data ||
          err.message
      );

      toast.error(
        "Server error while adding item"
      );
    }
  };

  /* =========================================================
     ✅ REMOVE FROM CART
  ========================================================= */

  const removeFromCart = async (
    product
  ) => {

    const productId =
      product.TestID ??
      product.productId ??
      product.packageId ??
      product.id;

    const categoryId =
      product.categoryId ?? 2;

    const payload = {

      userId: user.userId,

      productId,

      categoryId,

      totalQuantity: -1,
    };

    console.log(
      "REMOVE PAYLOAD:",
      payload
    );

    try {

      const resp = await axios.post(
        CART_API,
        payload
      );

      if (
        resp?.data?.succeeded ||
        resp?.status === 200
      ) {

        dispatch(
          removeItem({
            productId,
            categoryId,
          })
        );

        // ✅ REFRESH CART
        const { items } =
          await getCartByUserId(
            user.userId
          );

        console.log(
          "🛒 AFTER REMOVE:",
          items
        );

        dispatch(
          setCart(items || [])
        );

        toast.info(
          "Item removed ❌"
        );
      }

    } catch (err) {

      console.error(err);
    }
  };

  /* =========================================================
     ✅ TOTALS
  ========================================================= */

  const totalItems = cart.reduce(
    (sum, i) =>
      sum + i.quantity,
    0
  );

  const totalAmount = cart.reduce(
    (sum, i) =>
      sum + i.price * i.quantity,
    0
  );

  /* =========================================================
     ✅ CONTEXT VALUE
  ========================================================= */

  const contextValue = useMemo(
    () => ({
      cart,

      addToCart,

      removeFromCart,

      setCart,

      totalItems,

      totalAmount,

      deleteCartOnServer,
    }),

    [
      cart,
      totalItems,
      totalAmount,
    ]
  );

  return (
    <CartContext.Provider
      value={contextValue}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () =>
  useContext(CartContext);